import {
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import Image from "mui-image";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Delete, Settings } from "@mui/icons-material";
import { useAPITokens } from "Hooks/useAPITokens";
import {
  ApiAPITokenType,
  ApiAPITokenValueType,
  ApiProductTaggerUniqueField,
} from "@incendium/api";
import { useSave } from "Hooks/useSave";
import produce from "immer";
import { motion } from "motion/react";
import APITokenDrawer from "features/dataConnectors/components/APITokenDrawer";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";
import {
  createAPIToken,
  deleteToken,
  updateAPITokenValues,
} from "features/dataConnectors/components/apiTokenService";
import { useNotification } from "Hooks";
import ShopifyInstructions from "features/dataConnectors/components/ShopifyInstructions";
import { ApiURL, ProductTaggerUniqueFields } from "config";
import { formatEnumVal } from "Helpers/enumToText";

interface IAPITokenCardProps {
  type: ApiAPITokenType;
  clientId: number;
  projectId: number;
  projectKey: string;
  logo: string;
  title: string;
  info: React.ReactElement<any> | number | string;
  hideURL?: boolean;
  imageSx?: SxProps<Theme>;
  drawerMaxWidth?: number;
}

function APITokenCard({
  type,
  clientId,
  projectId,
  projectKey,
  logo,
  title,
  info,
  imageSx,
  drawerMaxWidth = 600,
}: IAPITokenCardProps) {
  const { tokens, setTokens } = useAPITokens();
  const { saving, save } = useSave();
  const token = useMemo(() => {
    return tokens.find((t) => t.type === type);
  }, [tokens, type]);
  const [orderPath, setOrderPath] = useState("order-recieved");
  const [uniqueField, setUniqueField] = useState(
    ApiProductTaggerUniqueField.SKU
  );
  const [openConfig, setOpenConfig] = useState(false);
  const { showSuccessNotification, showErrorNotification } = useNotification();

  useEffect(() => {
    if (!token) {
      return;
    }
    const orderValue =
      (token.values || []).find(
        (v) =>
          v.type ===
          ApiAPITokenValueType.API_TOKEN_VALUE_TYPE_WOOCOMMERCE_ORDER_PATH
      )?.value || "order-recieved";
    setOrderPath(orderValue);
    const uniqueValue =
      (token.values || []).find(
        (v) =>
          v.type ===
          ApiAPITokenValueType.API_TOKEN_VALUE_TYPE_PRODUCT_UNIQUE_FIELD
      )?.value || ApiProductTaggerUniqueField.SKU;
    setUniqueField(uniqueValue as ApiProductTaggerUniqueField);
  }, [token]);

  const newToken = useCallback(async () => {
    const res = await createAPIToken(clientId, type, [
      {
        type: ApiAPITokenValueType.API_TOKEN_VALUE_TYPE_PROJECT_ID,
        value: `${projectId as number}`,
      },
    ]);
    setTokens(
      produce(tokens, (draft) => {
        draft.push(res);
      })
    );
  }, [clientId, projectId, setTokens, tokens, type]);

  const removeToken = useCallback(
    async (id: number) => {
      await deleteToken(clientId, id);
      setTokens(
        produce(tokens, (draft) => {
          const idx = tokens.findIndex((t) => t.id === id);
          draft.splice(idx, 1);
        })
      );
    },
    [clientId, tokens, setTokens]
  );

  const handleChecked = useCallback(
    async (checked: boolean) => {
      save(async () => {
        if (checked) {
          await newToken();
          setOpenConfig(true);
        } else if (token) {
          removeToken(token?.id as number);
        }
      });
    },
    [newToken, removeToken, save, token]
  );

  const handleOnConfigure = useCallback(async () => {
    setOpenConfig(true);
  }, []);

  const saveSetting = useCallback(
    async (type: ApiAPITokenValueType, value: any) => {
      if (!token || !token?.id) {
        return;
      }

      let values = (token.values || []).map((v) => ({ ...v }));
      const idx = (values || []).findIndex((v) => v.type === type);
      if (idx >= 0) {
        values![idx].value = value;
      } else {
        values.push({
          type,
          value,
        });
      }
      const res = await updateAPITokenValues(clientId, token!.id, values);
      setTokens((tokens) =>
        produce(tokens, (draft) => {
          const idx = draft.findIndex((d) => d.id === res.id);
          if (idx >= 0) {
            draft[idx] = res;
          }
        })
      );
    },
    [clientId, token, setTokens]
  );

  const saveOrderPath = useCallback(async () => {
    try {
      saveSetting(
        ApiAPITokenValueType.API_TOKEN_VALUE_TYPE_WOOCOMMERCE_ORDER_PATH,
        orderPath
      );
      showSuccessNotification("Order Path Updated");
    } catch (error) {
      showErrorNotification("Internal Error, please try again");
    }
  }, [orderPath, showSuccessNotification, showErrorNotification, saveSetting]);
  const saveUniqueValue = useCallback(async () => {
    try {
      saveSetting(
        ApiAPITokenValueType.API_TOKEN_VALUE_TYPE_PRODUCT_UNIQUE_FIELD,
        uniqueField
      );
      showSuccessNotification("Unique Field Updated");
    } catch (error) {
      showErrorNotification("Internal Error, please try again");
    }
  }, [
    uniqueField,
    showSuccessNotification,
    showErrorNotification,
    saveSetting,
  ]);

  return (
    <>
      <GlassCard boxProps={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent={"space-between"}>
            <motion.div
              initial={{
                opacity: 0,
                x: -50,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              transition={{ delay: 0.6 }}
            >
              <Image
                height={40}
                src={logo}
                duration={50}
                fit={"contain"}
                objectPosition={"left"}
                sx={{ objectPosition: "left", ...imageSx }}
              />
            </motion.div>
            {!!token && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Settings />}
                onClick={handleOnConfigure}
              >
                configure
              </Button>
            )}

            {saving && <CircularProgress size={"28px"} />}
          </Stack>
          <Divider />
          <Box sx={{ height: 76 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1">{title}</Typography>

              {!!token && <Check color={"success"} fontSize={"small"} />}
            </Stack>

            {info}
          </Box>
          <Divider />
          <Box>
            {!!token ? (
              <Button
                size="small"
                color="secondary"
                startIcon={<Delete />}
                onClick={() => handleChecked(false)}
              >
                Remove Webhook
              </Button>
            ) : (
              <Button size="small" onClick={() => handleChecked(true)}>
                Generate Webhook
              </Button>
            )}
          </Box>
        </Stack>
      </GlassCard>
      <APITokenDrawer
        open={openConfig}
        onClose={() => setOpenConfig(false)}
        token={token}
        maxWidth={drawerMaxWidth}
      >
        <Box sx={{ width: drawerMaxWidth }}>
          {type === ApiAPITokenType.API_TOKEN_TYPE_WOOCOMMERCE && (
            <>
              <AccordianChartBuilderSidebarBlock
                title="Order Path"
                subTitle={`By default woocommerce uses the order path "order-received" if you have changed this please specify here.`}
              >
                <Stack direction={"row"} spacing={2}>
                  <TextField
                    label="order path"
                    name="order-path"
                    size="small"
                    fullWidth
                    value={orderPath}
                    onChange={(e) => {
                      setOrderPath(e.target.value);
                    }}
                  />
                  <Button onClick={saveOrderPath} size="small">
                    Save
                  </Button>
                </Stack>
              </AccordianChartBuilderSidebarBlock>
              <AccordianChartBuilderSidebarBlock
                title="Unique Field"
                subTitle={`By default we use the product SKU from the woocommerce webhook to mark a product as unique, if you would like to change this please select a field from below`}
              >
                <Stack direction={"row"} spacing={2}>
                  <TextField
                    fullWidth
                    value={uniqueField}
                    variant="outlined"
                    size="small"
                    select
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUniqueField(
                        e.target.value as ApiProductTaggerUniqueField
                      )
                    }
                  >
                    {ProductTaggerUniqueFields.map((item) => (
                      <MenuItem key={item} value={item}>
                        {formatEnumVal(item)}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button onClick={saveUniqueValue} size="small">
                    Save
                  </Button>
                </Stack>
              </AccordianChartBuilderSidebarBlock>
            </>
          )}
          {type === ApiAPITokenType.API_TOKEN_TYPE_SHOPIFY && token && (
            <AccordianChartBuilderSidebarBlock
              title="Setup Instructions"
              subTitle={`See instructions to Setup Shopify webhook and advanced Pixel Tracking`}
            >
              <ShopifyInstructions
                webhookUrl={token.url || ""}
                incUrl={`${ApiURL}/firefly/${projectKey}/incendium.js`}
              />
            </AccordianChartBuilderSidebarBlock>
          )}
        </Box>
      </APITokenDrawer>
    </>
  );
}

export default APITokenCard;
